import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import {
  Boxes,
  Building2,
  CheckCircle2,
  FileCheck2,
  FileStack,
  Lock,
  MessageCircle,
  UserRound,
  Workflow,
  LayoutDashboard,
  Calendar,
  Clock
} from 'lucide-react';

const Typewriter = ({ words, speed = 80, delay = 1500 }) => {
  const [index, setIndex] = useState(0);
  const [subIndex, setSubIndex] = useState(0);
  const [reverse, setSubReverse] = useState(false);

  useEffect(() => {
    if (subIndex === words[index].length + 1 && !reverse) {
      setTimeout(() => setSubReverse(true), delay);
      return;
    }

    if (subIndex === 0 && reverse) {
      setSubReverse(false);
      setIndex((prev) => (prev + 1) % words.length);
      return;
    }

    const timeout = setTimeout(() => {
      setSubIndex((prev) => prev + (reverse ? -1 : 1));
    }, reverse ? speed / 2 : speed);

    return () => clearTimeout(timeout);
  }, [subIndex, index, reverse, words, speed, delay]);

  return (
    <span className="text-[#2fc6f6] border-r-2 border-[#2fc6f6] pr-1 animate-pulse">
      {words[index].substring(0, subIndex)}
    </span>
  );
};

const PageHeader = () => {
  const { t } = useTranslation();
  return (
    <header className="fixed top-0 w-full bg-white/95 backdrop-blur-md z-50 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center flex-shrink-0">
          <img
            src="https://imgur.com/nYWIvWJ.png"
            alt="Nextcoders Logo"
            className="h-8"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://placehold.co/150x32/1f2937/ffffff?text=Nextcoders';
            }}
          />
        </Link>
        <div className="flex items-center gap-4 md:gap-8">
          <LanguageSwitcher variant="compact" />
          <Link
            to="/apps"
            className="flex-shrink-0 bg-[#141414] text-white px-4 md:px-6 py-2 rounded-lg hover:bg-gray-800 transition text-sm font-semibold text-center"
          >
            {t('header.all_apps')}
          </Link>
        </div>
      </div>
    </header>
  );
};

const Punku24Page = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="relative min-h-screen bg-white font-inter text-gray-900 overflow-x-hidden">
      <PageHeader />
      
      <main className="pt-20">
        {/* HERO SECTION - Pure Value & Breathing Space */}
        <section className="relative py-16 md:py-24 overflow-hidden bg-white">
          <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
            <div className="text-sm font-black tracking-[0.5em] text-[#2fc6f6] uppercase mb-6">{t('punku24.badge')}</div>
            <h1 className="text-5xl md:text-[80px] font-extrabold tracking-tighter text-gray-900 leading-[0.9] mb-10">
              {t('punku24.hero_title')} <br/>
              <span className="text-[#2fc6f6]">{t('punku24.hero_title_accent')}</span>
            </h1>
            <p className="text-xl md:text-3xl leading-relaxed text-gray-400 max-w-4xl mx-auto font-medium mb-12">
              {t('punku24.hero_subtitle')} <Typewriter words={[
                t('punku24.typewriter.clients'),
                t('punku24.typewriter.providers'),
                t('punku24.typewriter.contractors'),
                t('punku24.typewriter.patients')
              ]} /> {t('punku24.hero_subtitle_end')}
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="#how-it-works"
                className="bg-[#2fc6f6] text-white hover:bg-[#25b5e1] font-black py-4 px-10 rounded-2xl transition-all shadow-xl shadow-blue-200 text-base uppercase tracking-widest"
              >
                {t('punku24.cta_how_it_works')}
              </a>
              <button
                type="button"
                onClick={() => navigate('/apps')}
                className="bg-white border-2 border-gray-100 text-gray-900 hover:bg-gray-50 font-black py-4 px-10 rounded-2xl transition-all text-base uppercase tracking-widest"
              >
                {t('punku24.cta_free_trial')}
              </button>
            </div>
          </div>
          
          {/* Abstract Background Element */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1200px] h-[600px] bg-blue-50/50 blur-[120px] rounded-full -z-10 opacity-60" />
        </section>

        {/* SECTION 1: THE BRIDGE (The Internal Control) */}
        <section id="how-it-works" className="py-24 bg-gray-50/50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
              <div className="space-y-8 text-left">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-600 text-xs font-black uppercase tracking-widest">
                  {t('punku24.phase1_badge')}
                </div>
                <h2 className="text-4xl md:text-6xl font-extrabold text-gray-900 leading-tight">
                  {t('punku24.phase1_title')} <br/>{t('punku24.phase1_title_accent')}
                </h2>
                <p className="text-xl text-gray-500 leading-relaxed font-medium">
                  {t('punku24.phase1_desc')}
                </p>
                <div className="space-y-4 pt-4">
                  {[
                    <>{t('punku24.phase1_feature1')} <Typewriter words={t('punku24.phase1_feature1_typewriter', { returnObjects: true })} /> {t('punku24.phase1_feature1_end')}</>,
                    t('punku24.phase1_feature2'),
                    t('punku24.phase1_feature3'),
                    t('punku24.phase1_feature4')
                  ].map((text, i) => (
                    <div key={i} className="flex items-center gap-4 text-gray-700 font-bold text-[13px] leading-tight">
                      <div className="w-5 h-5 rounded-full bg-emerald-50 text-emerald-500 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 size={14} />
                      </div>
                      {text}
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <div className="rounded-[40px] border border-gray-200 bg-white p-8 md:p-12 shadow-2xl">
                  <div className="flex items-center justify-between mb-10 pb-6 border-b border-gray-50">
                    <div className="flex items-center gap-4 text-left">
                      <div className="w-12 h-12 rounded-2xl bg-blue-500 text-white flex items-center justify-center shadow-lg">
                        <Workflow size={24} />
                      </div>
                      <div>
                        <div className="text-lg font-black text-gray-900 leading-none">{t('punku24.mockup_project_management')}</div>
                        <div className="text-xs text-blue-500 font-bold mt-1 uppercase tracking-widest">{t('punku24.mockup_env')}</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-8">
                    <div className="p-6 rounded-3xl bg-blue-50/50 border border-blue-100 text-left">
                      <div className="text-xs font-black text-blue-600 uppercase tracking-widest mb-4">{t('punku24.mockup_config')}</div>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-4 rounded-xl bg-white shadow-sm">
                          <span className="text-sm font-bold">{t('punku24.mockup_field1')}</span>
                          <CheckCircle2 size={18} className="text-emerald-500" />
                        </div>
                        <div className="flex items-center justify-between p-4 rounded-xl bg-white shadow-sm">
                          <span className="text-sm font-bold">{t('punku24.mockup_field2')}</span>
                          <CheckCircle2 size={18} className="text-emerald-500" />
                        </div>
                        <div className="flex items-center justify-between p-4 rounded-xl bg-white shadow-sm">
                          <span className="text-sm font-bold">{t('punku24.mockup_field3')}</span>
                          <CheckCircle2 size={18} className="text-emerald-500" />
                        </div>
                      </div>
                    </div>
                    
                    <button className="w-full py-5 rounded-2xl bg-gray-900 text-white font-black hover:bg-gray-800 transition-all text-lg shadow-xl uppercase tracking-widest">
                      {t('punku24.mockup_cta')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2: THE PORTAL (The Collaborator Experience) */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center text-left">
              <div className="order-2 lg:order-1 relative">
                <div className="rounded-[48px] border border-gray-200 bg-white shadow-[0_80px_160px_-40px_rgba(0,0,0,0.1)] overflow-hidden">
                  {/* Browser Header */}
                  <div className="h-14 px-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-rose-400" />
                      <div className="w-3 h-3 rounded-full bg-amber-400" />
                      <div className="w-3 h-3 rounded-full bg-emerald-400" />
                    </div>
                    <div className="px-3 py-1 rounded-full bg-white border border-gray-200 text-[10px] font-bold text-gray-400">
                      portal.tuempresa.com
                    </div>
                  </div>
                  
                  <div className="flex">
                    {/* Sidebar */}
                    <div className="w-20 border-r border-gray-100 bg-gray-50/30 p-4 space-y-6 flex flex-col items-center">
                      <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center text-white shadow-lg">
                        <Building2 size={20} />
                      </div>
                      <div className="space-y-4">
                        <div className="w-8 h-8 rounded-lg bg-white border border-gray-100 flex items-center justify-center text-gray-400">
                          <LayoutDashboard size={16} />
                        </div>
                        <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-500">
                          <FileCheck2 size={16} />
                        </div>
                        <div className="w-8 h-8 rounded-lg bg-white border border-gray-100 flex items-center justify-center text-gray-400">
                          <Calendar size={16} />
                        </div>
                        <div className="w-8 h-8 rounded-lg bg-white border border-gray-100 flex items-center justify-center text-gray-400">
                          <Clock size={16} />
                        </div>
                      </div>
                    </div>

                    {/* Main Content */}
                    <div className="flex-1 p-8 bg-white">
                      <div className="flex justify-between items-start mb-10">
                        <div>
                          <h3 className="text-2xl font-black text-gray-900">{t('punku24.portal_status_project')}</h3>
                          <p className="text-sm text-gray-500 font-medium">Global Solutions Inc.</p>
                        </div>
                        <div className="px-4 py-1.5 rounded-full bg-emerald-50 text-emerald-600 text-[11px] font-bold uppercase tracking-wider">
                          {t('punku24.portal_status_ongoing')}
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4 mb-10">
                        {[
                          { label: t('punku24.portal_stat_milestones'), val: '12/15', color: 'text-blue-600' },
                          { label: t('punku24.portal_stat_files'), val: '08', color: 'text-indigo-600' },
                          { label: t('punku24.portal_stat_days'), val: '24', color: 'text-amber-600' }
                        ].map((stat, i) => (
                          <div key={i} className="p-4 rounded-2xl bg-gray-50 border border-gray-100">
                            <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{stat.label}</div>
                            <div className={`text-xl font-black mt-1 ${stat.color}`}>{stat.val}</div>
                          </div>
                        ))}
                      </div>

                      <div className="space-y-6">
                        <div className="flex items-center justify-between p-5 rounded-2xl border border-gray-100 bg-white shadow-sm">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-500 flex items-center justify-center">
                              <FileStack size={20} />
                            </div>
                            <span className="text-sm font-bold text-gray-700">{t('punku24.portal_file_signed')}</span>
                          </div>
                          <button className="px-4 py-2 rounded-lg bg-gray-900 text-white text-[11px] font-black uppercase tracking-wider">
                            {t('punku24.portal_btn_sign')}
                          </button>
                        </div>
                        
                        <div className="p-5 rounded-2xl border border-gray-100 bg-white shadow-sm">
                          <div className="flex items-center gap-3 mb-4">
                            <MessageCircle size={16} className="text-blue-500" />
                            <span className="text-xs font-black text-gray-400 uppercase tracking-widest">{t('punku24.portal_chat_title')}</span>
                          </div>
                          <div className="space-y-3">
                            <div className="flex gap-3">
                              <div className="w-8 h-8 rounded-full bg-gray-100 flex-shrink-0" />
                              <div className="p-3 rounded-2xl bg-gray-50 text-xs font-medium text-gray-600">
                                {t('punku24.portal_chat_msg1')}
                              </div>
                            </div>
                            <div className="flex flex-row-reverse gap-3">
                              <div className="w-8 h-8 rounded-full bg-blue-500 flex-shrink-0" />
                              <div className="p-3 rounded-2xl bg-blue-500 text-white text-xs font-medium">
                                {t('punku24.portal_chat_msg2')}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="order-1 lg:order-2 space-y-8">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 text-emerald-600 text-xs font-black uppercase tracking-widest">
                  {t('punku24.phase2_badge')}
                </div>
                <h2 className="text-4xl md:text-6xl font-extrabold text-gray-900 leading-tight">
                  {t('punku24.phase2_title')} <br/>{t('punku24.phase2_title_accent')}
                </h2>
                <p className="text-xl text-gray-500 leading-relaxed font-medium">
                  {t('punku24.phase2_desc')}
                </p>
                <div className="space-y-6 pt-6">
                  <div className="p-8 rounded-3xl border border-gray-100 bg-gray-50/50">
                    <div className="flex items-center gap-6">
                      <div className="w-14 h-14 rounded-2xl bg-white shadow-md flex items-center justify-center text-amber-500">
                        <Lock size={28} />
                      </div>
                      <div>
                        <div className="text-lg font-black text-gray-900">{t('punku24.phase2_feature1_title')}</div>
                        <p className="text-gray-500 font-medium mt-1">{t('punku24.phase2_feature1_desc')}</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-8 rounded-3xl border border-gray-100 bg-gray-50/50">
                    <div className="flex items-center gap-6">
                      <div className="w-14 h-14 rounded-2xl bg-white shadow-md flex items-center justify-center text-indigo-500">
                        <MessageCircle size={28} />
                      </div>
                      <div>
                        <div className="text-lg font-black text-gray-900">{t('punku24.phase2_feature2_title')}</div>
                        <p className="text-gray-500 font-medium mt-1">{t('punku24.phase2_feature2_desc')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 3: USE CASES - Refined & Structured */}
        <section className="py-32 bg-white relative">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-20 items-end mb-20 text-left">
              <div>
                <div className="text-sm font-black tracking-[0.4em] text-[#2fc6f6] uppercase mb-6">{t('punku24.use_cases_badge')}</div>
                <h2 className="text-5xl md:text-8xl font-extrabold text-gray-900 tracking-tighter leading-none">
                  {t('punku24.use_cases_title')} <br/>{t('punku24.use_cases_title_accent')}
                </h2>
              </div>
              <p className="text-xl text-gray-500 font-medium leading-relaxed pb-2">
                {t('punku24.use_cases_desc')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-100 border border-gray-100 rounded-[48px] overflow-hidden shadow-sm text-left">
              {[
                {
                  key: 'clients',
                  icon: UserRound,
                  colors: [
                    { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-100' },
                    { bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-100' },
                    { bg: 'bg-cyan-50', text: 'text-cyan-600', border: 'border-cyan-100' }
                  ]
                },
                {
                  key: 'providers',
                  icon: Building2,
                  colors: [
                    { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-100' },
                    { bg: 'bg-teal-50', text: 'text-teal-600', border: 'border-teal-100' },
                    { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-100' }
                  ]
                },
                {
                  key: 'contractors',
                  icon: FileCheck2,
                  colors: [
                    { bg: 'bg-violet-50', text: 'text-violet-600', border: 'border-violet-100' },
                    { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-100' },
                    { bg: 'bg-fuchsia-50', text: 'text-fuchsia-600', border: 'border-fuchsia-100' }
                  ]
                },
                {
                  key: 'patients',
                  icon: Boxes,
                  colors: [
                    { bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-100' },
                    { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-100' },
                    { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-100' }
                  ]
                }
              ].map((item, i) => {
                const data = t(`punku24.use_cases.${item.key}`, { returnObjects: true });
                return (
                  <div key={i} className="bg-white p-12 md:p-16 hover:bg-gray-50/50 transition-colors group">
                    <div className="flex flex-col h-full">
                      <div className="w-14 h-14 rounded-2xl bg-gray-900 text-white flex items-center justify-center mb-10 group-hover:scale-110 transition-transform duration-500 shadow-xl shadow-gray-200">
                        <item.icon size={24} />
                      </div>
                      
                      <h3 className="text-3xl font-extrabold text-gray-900 mb-6">{data.title}</h3>
                      <p className="text-lg text-gray-500 leading-relaxed font-medium mb-10 flex-1">
                        {data.desc}
                      </p>
                      
                      <div className="flex flex-wrap gap-2">
                        {data.tags.map((label, j) => (
                          <span key={j} className={`px-4 py-1.5 rounded-full ${item.colors[j].bg} ${item.colors[j].text} text-[10px] font-black uppercase tracking-widest border ${item.colors[j].border}`}>
                            {label}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-24 bg-white overflow-hidden relative text-center border-t border-gray-50">
          <div className="max-w-5xl mx-auto px-6 text-center relative z-10">
            <h2 className="text-5xl md:text-8xl font-extrabold text-gray-900 tracking-tighter leading-none mb-10">
              {t('punku24.final_cta_title')} <br/> {t('punku24.final_cta_title_accent')}
            </h2>
            <p className="text-2xl text-gray-400 font-medium mb-16 max-w-2xl mx-auto">
              {t('punku24.final_cta_desc')}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="#cta"
                className="bg-[#2fc6f6] text-white hover:bg-[#25b5e1] font-black py-4 px-10 rounded-2xl transition-all shadow-xl shadow-blue-200 text-base uppercase tracking-widest"
              >
                {t('punku24.final_cta_btn')}
              </a>
              <button
                onClick={() => navigate('/apps')}
                className="bg-white border-2 border-gray-100 text-gray-900 hover:bg-gray-50 font-black py-4 px-10 rounded-2xl transition-all text-base uppercase tracking-widest"
              >
                {t('punku24.final_cta_other')}
              </button>
            </div>
          </div>
          
          <div className="absolute -bottom-48 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-blue-50 blur-[100px] rounded-full -z-10" />
        </section>
      </main>
    </div>
  );
};

export default Punku24Page;
